import { useNavigate, useParams } from 'react-router-dom';
import React, { useState, useEffect, useMemo } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  Clock,
  User,
  Video,
  Code2,
  FileText,
  MessageSquare,
  Star,
  Play,
  Share2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Loader2,
  Edit,
  ChevronDown,
  ChevronUp,
  Palette,
  Server,
  Database,
  Layers,
  Zap,
  Network,
  StickyNote
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import SystemDesignViewer from '@/components/interview/SystemDesignViewer'
import WhiteboardViewer from '@/components/interview/WhiteboardViewer'
import EvaluationForm from '@/components/interview/EvaluationForm'
import EvaluationDisplay from '@/components/interview/EvaluationDisplay'
import DataModelingViewer from '@/components/interview/DataModelingViewer'
import UMLDesignViewer from '@/components/interview/UMLDesignViewer'
import ShareInterviewModal from '@/components/interview/ShareInterviewModal'


interface CandidateInput {
  id: number
  type: string
  category: string
  content: string
  problem_id?: number
  problem_title?: string
  save_type: string
  last_save_at?: string
  created_at?: string
  metadata?: any
  file_url?: string
}

interface DatabaseChallengeSubmission {
  submission_uuid?: string
  submitted_query?: string
  execution_result?: any
  execution_error?: string | null
  is_correct?: boolean
  score?: number | null
  submission_status?: string
  submitted_at?: string
}

interface DatabaseChallenge {
  assignment_uuid: string
  challenge_uuid: string
  title: string
  description?: string
  difficulty?: string
  challenge_type?: string
  database_type?: string
  schema_setup?: string
  expected_result?: any
  hints?: any
  time_limit_minutes?: number
  assignment_order?: number
  submission?: DatabaseChallengeSubmission | null
}

interface DataModelingAttribute {
  id?: string
  name?: string
  type?: string
  isPrimary?: boolean
  isForeign?: boolean
  isRequired?: boolean
  defaultValue?: string
  referencedTableId?: string
  referencedColumnId?: string
}

interface DataModelingEntityRecord {
  id?: string | number
  name?: string
  attributes?: DataModelingAttribute[]
  fields?: DataModelingAttribute[]
  description?: string
  x?: number | string
  y?: number | string
  width?: number | string
  height?: number | string
  position?: {
    x?: number | string
    y?: number | string
  }
}

interface DataModelingRelationshipRecord {
  id?: string
  fromEntityId?: string
  toEntityId?: string
  type?: string
  label?: string
}

interface DataModelingForeignKeyRecord {
  id?: string
  fromEntityId?: string
  toEntityId?: string
  fromAttributeId?: string
  toAttributeId?: string
  relationshipType?: string
  label?: string
}

interface DataModelingCommentRecord {
  id?: string
  text?: string
  color?: string
  created_at?: string
  createdAt?: string
  x?: number | string
  y?: number | string
  width?: number | string
  height?: number | string
}

interface DataModelingEntry {
  id?: number
  uuid?: string
  version?: number
  version_number?: number
  content?: string
  entities: DataModelingEntityRecord[]
  relationships?: DataModelingRelationshipRecord[]
  foreignKeys: DataModelingForeignKeyRecord[]
  comments: DataModelingCommentRecord[]
  zoomPan?: {
    zoom?: number
    panX?: number
    panY?: number
  } | null
  savedAt?: string | null
  isCompleted?: boolean
  completionSeconds?: number | null
  saveType?: string
  wordCount?: number | null
}

interface UmlAttributeRecord {
  id?: string
  name?: string
  type?: string
  visibility?: string
}

interface UmlMethodRecord {
  id?: string
  name?: string
  returnType?: string
  parameters?: string
  visibility?: string
}

interface UmlClassRecord {
  id: string
  name: string
  type: string
  x: number
  y: number
  width: number
  height: number
  attributes: UmlAttributeRecord[]
  methods: UmlMethodRecord[]
}

interface UmlRelationshipRecord {
  id: string
  fromClassId: string
  toClassId: string
  type: string
  label?: string
}

interface UmlDesignEntry {
  id?: number
  uuid?: string
  version_number?: number
  version?: number
  diagram_type?: string
  content?: string
  classes: UmlClassRecord[]
  relationships: UmlRelationshipRecord[]
  is_completed?: boolean
  completion_time_seconds?: number | null
  completionSeconds?: number | null
  saved_at?: string | null
  savedAt?: string | null
}

interface SystemDesignComponentRecord {
  id: string
  componentId: string
  name: string
  color: string
  x: number
  y: number
  width: number
  height: number
}

interface SystemDesignConnectionRecord {
  id: string
  fromComponentId: string
  toComponentId: string
  color: string
  strokeWidth: number
  type: string
  label?: string
}

interface SystemDesignEntry {
  id?: number
  uuid?: string
  version?: number
  savedAt?: string | null
  content?: string
  diagramElements: {
    placedComponents: SystemDesignComponentRecord[]
    connections: SystemDesignConnectionRecord[]
    canvasSize?: {
      width?: number
      height?: number
    }
    viewport?: {
      zoom?: number
      panX?: number
      panY?: number
    }
  }
  designNotes?: string
  discussion?: string
}

interface WhiteboardEntry {
  id: string
  uuid?: string
  saved_at?: string | null
  savedAt?: string | null
  version?: number | null
  version_number?: number | null
  versionNumber?: number | null
  input_type?: string
  input_category?: string
  action_type?: string
  content?: string
  canvas_elements?: any
  canvasElements?: any
  whiteboard_text_discussion?: string | null
  whiteboardTextDiscussion?: string | null
  stroke_data?: any
  strokeData?: any
}

interface InterviewResponse {
  id: string
  title: string
  interview_type: string
  format: string
  stage: string
  scheduled_at: string
  duration_minutes: number
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled' | 'no-show'
  overall_rating?: number
  recommendation?: string
  notes?: string
  meeting_url?: string
  is_draft?: boolean
  created_at: string
  updated_at: string
  
  // Candidate information
  candidate: {
    id: number
    uuid?: string
    name: string
    first_name: string
    last_name: string
    email: string
    phone?: string
    current_title?: string
    current_company?: string
    location?: string
    experience_years?: number
    skills: string[]
    linkedin_url?: string
    github_url?: string
    portfolio_url?: string
    resume_url?: string
  }
  
  // Position information
  position: {
    id: number
    title: string
    description?: string
    location?: string
    employment_type?: string
    salary_range?: string
    department?: string
  }
  
  // Interviewer information
  interviewer: {
    id: number
    name: string
    first_name: string
    last_name: string
    email: string
    title?: string
    phone?: string
  }
  
  // Company information
  company: {
    name: string
  }
  
  // Interview session data
  session?: {
    session_id: string
    status: string
    started_at?: string
    ended_at?: string
    total_time_spent?: number
    completion_percentage?: number
    settings: any
  }
  
  // Coding problems and responses
  problems: {
    assignment_id: number
    problem_id: number
    title: string
    description: string
    difficulty: string
    category: string
    time_limit_seconds: number
    allowed_languages: string[]
    assignment_order: number
    assigned_at?: string
    completed_at?: string
    editorial_solution?: string
    solution?: Record<string, string>
    submissions: {
      id: number
      language: string
      code: string
      submitted_at?: string
      execution_time?: number
      memory_used?: number
      status?: string
      test_results?: any
      passed_tests?: number
      total_tests?: number
      score?: number
      feedback?: string
    }[]
  }[]
  
  // Other candidate inputs
  candidate_inputs: CandidateInput[]
  
  // Recording data
  recordings: {
    id: number
    type: string
    file_url: string
    duration_seconds: number
    recorded_at?: string
    file_size?: number
    thumbnail_url?: string
    status: string
  }[]
  
  // Whiteboard data
  whiteboard_data: {
    id: number
    saved_at?: string
    version: number
    input_type: string
    input_category?: string
    content?: string
    canvas_elements?: any
    canvas_size?: any
    diagram_elements?: any
    design_notes?: string
    whiteboard_text_discussion?: string
    action_type: string
    stroke_data?: any
    text_data?: any
  }[]
  
  // System design data
  system_design?: {
    id: number
    saved_at?: string
    version: number
    input_category?: string
    content?: string
    diagram_elements?: any
    design_notes?: string
    canvas_elements?: any
    canvas_size?: any
    system_design_discussion?: string
  }[]

  // UML design data
  uml_design?: {
    id: number
    uuid: string
    saved_at?: string
    version_number: number
    content?: string
    classes_data?: any
    relationships_data?: any
    diagram_type: string
    is_completed: boolean
    completion_time_seconds?: number
  }[]

  // Data modeling responses
  data_modeling?: {
    id: number
    uuid: string
    saved_at?: string
    version_number: number
    content?: string
    entities_data?: any
    foreign_keys_data?: any
    comments_data?: any
    is_completed: boolean
    completion_time_seconds?: number
  }[]

  // Database challenge responses
  database_challenges?: DatabaseChallenge[]
}


export default function InterviewResponsePage() {
  const params = useParams()
  const navigate = useNavigate()
  const interviewId = params?.id as string
  
  const [response, setResponse] = useState<InterviewResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'overview' | 'coding' | 'databaseChallenge' | 'database' | 'design' | 'uml' | 'notes' | 'responses' | 'whiteboard' | 'recording'>('overview')
  const [expandedSolutions, setExpandedSolutions] = useState<Record<number, boolean>>({})
  const [databaseChallenges, setDatabaseChallenges] = useState<DatabaseChallenge[]>([])
  const [databaseChallengesLoading, setDatabaseChallengesLoading] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [databaseChallengesError, setDatabaseChallengesError] = useState<string | null>(null)
  const [umlDesignEntries, setUmlDesignEntries] = useState<UmlDesignEntry[]>([])
  const [umlDesignLoading, setUmlDesignLoading] = useState(false)
  const [umlDesignError, setUmlDesignError] = useState<string | null>(null)
  const [dataModelingEntries, setDataModelingEntries] = useState<DataModelingEntry[]>([])
  const [dataModelingLoading, setDataModelingLoading] = useState(false)
  const [dataModelingError, setDataModelingError] = useState<string | null>(null)
  const [systemDesignEntries, setSystemDesignEntries] = useState<SystemDesignEntry[]>([])
  const [systemDesignLoading, setSystemDesignLoading] = useState(false)
  const [systemDesignError, setSystemDesignError] = useState<string | null>(null)
  const [whiteboardEntries, setWhiteboardEntries] = useState<WhiteboardEntry[]>([])
  const [whiteboardLoading, setWhiteboardLoading] = useState(false)
  const [whiteboardError, setWhiteboardError] = useState<string | null>(null)
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesError, setNotesError] = useState<string | null>(null)

  const fetchInterviewNotes = async () => {
    if (!interviewId) return
    try {
      setNotesLoading(true)
      setNotesError(null)
      const resp = await apiClient.get('/interview-responses/public/load/notes', { interviewId })
      const noteData = resp?.data?.data?.response
      if (noteData && typeof noteData.content === 'string') {
        const noteEntry = {
          id: noteData.uuid || noteData.id,
          type: 'notes',
          category: 'notes',
          content: noteData.content,
          save_type: noteData.saveType || 'auto',
          last_save_at: noteData.lastSaveAt || noteData.lastUpdatedAt || null,
          created_at: noteData.firstInputAt || null,
          metadata: null,
          file_url: null,
          problem_id: null,
          problem_title: null
        }
        setResponse(prev => prev ? { ...prev, candidate_inputs: [...(prev.candidate_inputs || []).filter(ci => ci.category !== 'notes'), noteEntry] } as any : prev)
      }
    } catch (err) {
      setNotesError('Failed to load notes')
      console.error('Failed to load notes', err)
    } finally {
      setNotesLoading(false)
    }
  }

  useEffect(() => {
    if (interviewId) {
      loadInterviewResponse()
    }
  }, [interviewId])

  const loadInterviewResponse = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const apiResponse = await apiClient.getInterviewResponse(interviewId)
      
      if (apiResponse.success && apiResponse.data) {
        setResponse(apiResponse.data as InterviewResponse)
      } else {
        setError('Failed to load interview data')
      }
    } catch (err) {
      console.error('Failed to load interview response:', err)
      setError('Failed to load interview data')
    } finally {
      setLoading(false)
    }
  }

  const parseJsonField = (value: any) => {
    if (!value) return null
    if (typeof value === 'string') {
      try {
        return JSON.parse(value)
      } catch (jsonError) {
        console.warn('Failed to parse JSON field', jsonError)
        return value
      }
    }
    return value
  }

  const pickArray = (...sources: any[]): any[] => {
    for (const source of sources) {
      if (Array.isArray(source)) {
        return source
      }
    }
    return []
  }

  const toOptionalString = (value: any, fallback?: string): string | undefined => {
    if (value === undefined || value === null || value === '') {
      return fallback
    }
    try {
      return String(value)
    } catch {
      return fallback
    }
  }

  const parseBoolean = (value: any, defaultValue = false): boolean => {
    if (value === undefined || value === null) return defaultValue
    if (typeof value === 'boolean') return value
    if (typeof value === 'number') return value !== 0
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase()
      if (['true', '1', 'yes', 'y', 'on'].includes(normalized)) return true
      if (['false', '0', 'no', 'n', 'off'].includes(normalized)) return false
    }
    return defaultValue
  }

  const parseNumber = (value: any): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  const renderTextWithLinks = (text?: string | null) => {
    if (!text) return null

    const urlRegex = /(https?:\/\/[\w\-._~:/?#[\]@!$&'()*+,;=%]+)/g
    const nodes: React.ReactNode[] = []
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0]
      const index = match.index

      if (index > lastIndex) {
        nodes.push(
          <span key={`text-${index}`}>{text.slice(lastIndex, index)}</span>
        )
      }

      nodes.push(
        <a
          key={`link-${index}`}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 break-words"
        >
          {url}
        </a>
      )

      lastIndex = index + url.length
    }

    if (lastIndex < text.length) {
      nodes.push(
        <span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>
      )
    }

    return <span className="whitespace-pre-wrap break-words">{nodes}</span>
  }

  const loadDatabaseChallenges = async (interviewUuid: string) => {
    try {
      setDatabaseChallengesLoading(true)
      setDatabaseChallengesError(null)

      const apiResponse = await apiClient.getInterviewDatabaseChallenges(interviewUuid)

      if (apiResponse.success && Array.isArray(apiResponse.data)) {
        const formattedChallenges: DatabaseChallenge[] = apiResponse.data.map((challenge: any) => ({
          assignment_uuid: challenge.assignment_uuid,
          challenge_uuid: challenge.challenge_uuid,
          title: challenge.title,
          description: challenge.description,
          difficulty: challenge.difficulty,
          challenge_type: challenge.challenge_type,
          database_type: challenge.database_type,
          schema_setup: challenge.schema_setup,
          expected_result: parseJsonField(challenge.expected_result),
          hints: parseJsonField(challenge.hints),
          time_limit_minutes: challenge.time_limit_minutes,
          assignment_order: challenge.assignment_order,
          submission: challenge.submission_uuid
            ? {
                submission_uuid: challenge.submission_uuid,
                submitted_query: challenge.submitted_query,
                execution_result: parseJsonField(challenge.execution_result),
                execution_error: challenge.execution_error,
                is_correct: challenge.is_correct,
                score: challenge.score,
                submission_status: challenge.submission_status,
                submitted_at: challenge.submitted_at
              }
            : null
        }))

        setDatabaseChallenges(formattedChallenges)
      } else {
        setDatabaseChallenges([])
        if (apiResponse.error) {
          setDatabaseChallengesError(apiResponse.error)
        }
      }
    } catch (err) {
      console.error('Failed to load database challenges:', err)
      setDatabaseChallengesError('Failed to load database challenges')
    } finally {
      setDatabaseChallengesLoading(false)
    }
  }

  const mapDataModelingRecord = (record: any): DataModelingEntry => {
    const rawModelData = parseJsonField(
      record.database_modeling_data ??
      record.modeling_data ??
      record.model_data ??
      record.modelData
    )

    const rawEntities =
      parseJsonField(record.entities_data ?? record.entitiesData ?? record.entities) ??
      (rawModelData && typeof rawModelData === 'object' ? rawModelData : null)

    const rawForeignKeys =
      parseJsonField(record.foreign_keys_data ?? record.foreignKeysData ?? record.foreignKeys) ??
      (rawModelData && typeof rawModelData === 'object' ? rawModelData : null)

    const rawComments =
      parseJsonField(record.comments_data ?? record.commentsData ?? record.comments) ??
      (rawModelData && typeof rawModelData === 'object' ? rawModelData : null)

    const rawRelationships =
      parseJsonField(record.relationships_data ?? record.relationshipsData ?? record.relationships) ??
      (rawModelData && typeof rawModelData === 'object' ? rawModelData : null)

    const entityArray = pickArray(
      rawEntities,
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.entities : undefined),
      record.entities,
      record.entitiesData,
      (rawModelData && typeof rawModelData === 'object' ? rawModelData.entities : undefined)
    )

    const relationshipArray = pickArray(
      rawRelationships,
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.relationships : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.relations : undefined),
      record.relationships,
      record.relationshipsData,
      (rawModelData && typeof rawModelData === 'object' ? rawModelData.relationships : undefined)
    )

    const foreignKeyArray = pickArray(
      rawForeignKeys,
      (rawForeignKeys && typeof rawForeignKeys === 'object' ? rawForeignKeys.relationships : undefined),
      (rawForeignKeys && typeof rawForeignKeys === 'object' ? rawForeignKeys.foreignKeys : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.foreignKeys : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.fkRelationships : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.foreignKeyRelationships : undefined),
      (rawModelData && typeof rawModelData === 'object' ? rawModelData.fkRelationships : undefined)
    )

    const commentArray = pickArray(
      rawComments,
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.comments : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.notes : undefined),
      (rawEntities && typeof rawEntities === 'object' ? rawEntities.annotations : undefined),
      (rawModelData && typeof rawModelData === 'object' ? rawModelData.comments : undefined)
    )

    const normalizedEntities: DataModelingEntityRecord[] = entityArray.map((entity: any, entityIndex: number) => {
      const entityId = toOptionalString(
        entity?.id ?? entity?.uuid ?? entity?.key ?? entity?.identifier ?? entity?.entityId,
        `entity-${entityIndex}`
      )!
      const name = entity?.name ?? entity?.title ?? entity?.label ?? `Entity ${entityIndex + 1}`

      const attributeArray = pickArray(
        entity?.attributes,
        entity?.fields,
        entity?.columns,
        entity?.props,
        entity?.data?.attributes
      )

      const normalizedAttributes: DataModelingAttribute[] = attributeArray.map((attr: any, attrIndex: number) => {
        const attrId = toOptionalString(
          attr?.id ?? attr?.uuid ?? attr?.key ?? attr?.identifier ?? attr?.attributeId ?? attr?.columnId ?? attr?.name,
          `${entityId}-attr-${attrIndex}`
        )!

        return {
          ...attr,
          id: attrId,
          name: attr?.name ?? attr?.label ?? attr?.title ?? attr?.key ?? `field_${attrIndex + 1}`,
          type: attr?.type ?? attr?.dataType ?? attr?.fieldType ?? attr?.columnType ?? attr?.kind ?? 'TEXT',
          isPrimary: parseBoolean(attr?.isPrimary ?? attr?.primary ?? attr?.is_primary ?? attr?.primaryKey ?? attr?.isPrimaryKey ?? attr?.pk),
          isForeign: parseBoolean(attr?.isForeign ?? attr?.foreign ?? attr?.is_foreign ?? attr?.isForeignKey ?? attr?.foreignKey ?? attr?.fk),
          isRequired: parseBoolean(attr?.isRequired ?? attr?.required ?? attr?.is_required ?? attr?.notNull ?? attr?.mandatory),
          defaultValue: attr?.defaultValue ?? attr?.default ?? attr?.default_value ?? attr?.defaultValueExpression ?? attr?.defaultExpression
        }
      })

      return {
        ...entity,
        id: entityId,
        name,
        attributes: normalizedAttributes,
        fields: normalizedAttributes,
        description: entity?.description ?? entity?.summary ?? entity?.notes,
        x: parseNumber(entity?.x ?? entity?.position?.x ?? entity?.posX ?? entity?.left ?? entity?.layout?.x),
        y: parseNumber(entity?.y ?? entity?.position?.y ?? entity?.posY ?? entity?.top ?? entity?.layout?.y),
        width: parseNumber(entity?.width ?? entity?.size?.width ?? entity?.layout?.width),
        height: parseNumber(entity?.height ?? entity?.size?.height ?? entity?.layout?.height)
      }
    })

    const attributeOwnerMap = new Map<string, string>()
    normalizedEntities.forEach(entity => {
      const entityIdStr = toOptionalString(entity.id)
      if (!entityIdStr) return
      const attributeList = pickArray(entity.attributes, entity.fields)
      attributeList.forEach((attr: any) => {
        const attrIdStr = toOptionalString(attr?.id)
        if (attrIdStr) {
          attributeOwnerMap.set(attrIdStr, entityIdStr)
        }
      })
    })

    const normalizedRelationships: DataModelingRelationshipRecord[] = []
    
    relationshipArray.forEach((rel: any, relIndex: number) => {
      const fromEntityId = toOptionalString(
        rel?.fromEntityId ?? rel?.sourceEntityId ?? rel?.source?.entityId ?? rel?.sourceEntity ?? rel?.from?.entityId ?? rel?.entityA ?? rel?.parentEntityId ?? rel?.leftEntityId
      )
      const toEntityId = toOptionalString(
        rel?.toEntityId ?? rel?.targetEntityId ?? rel?.target?.entityId ?? rel?.targetEntity ?? rel?.to?.entityId ?? rel?.entityB ?? rel?.childEntityId ?? rel?.rightEntityId
      )

      if (fromEntityId && toEntityId) {
        normalizedRelationships.push({
          id: toOptionalString(rel?.id ?? rel?.uuid ?? rel?.key ?? rel?.identifier ?? relIndex, `rel-${relIndex}`),
          fromEntityId,
          toEntityId,
          type: rel?.type ?? rel?.relationshipType ?? rel?.kind ?? rel?.cardinality ?? rel?.direction ?? rel?.category,
          label: rel?.label ?? rel?.relationshipLabel ?? rel?.name ?? rel?.title ?? rel?.description
        })
      }
    })

    const relationshipDerivedForeignKeys = relationshipArray
      .filter((rel: any) => {
        const typeValue = (rel?.type ?? rel?.relationshipType ?? '').toString().toLowerCase()
        return parseBoolean(rel?.isForeignKey, false) || ['foreignkey', 'foreign_key', 'fk'].includes(typeValue)
      })
      .map((rel: any) => ({
        fromEntityId: rel?.fromEntityId ?? rel?.sourceEntityId ?? rel?.source?.entityId ?? rel?.entityA,
        toEntityId: rel?.toEntityId ?? rel?.targetEntityId ?? rel?.target?.entityId ?? rel?.entityB,
        fromAttributeId: rel?.fromAttributeId ?? rel?.sourceAttributeId ?? rel?.source?.attributeId,
        toAttributeId: rel?.toAttributeId ?? rel?.targetAttributeId ?? rel?.target?.attributeId,
        relationshipType: rel?.relationshipType ?? rel?.type,
        label: rel?.label ?? rel?.name
      }))

    const normalizedForeignKeys = [...foreignKeyArray, ...relationshipDerivedForeignKeys]
      .map<DataModelingForeignKeyRecord | null>((fk: any, fkIndex: number) => {
        let fromEntityId = toOptionalString(
          fk?.fromEntityId ?? fk?.sourceEntityId ?? fk?.source?.entityId ?? fk?.childEntityId ?? fk?.tableId ?? fk?.childTableId ?? fk?.from?.entityId
        )
        let toEntityId = toOptionalString(
          fk?.toEntityId ?? fk?.targetEntityId ?? fk?.target?.entityId ?? fk?.parentEntityId ?? fk?.referenceTableId ?? fk?.parentTableId ?? fk?.to?.entityId
        )
        const fromAttributeId = toOptionalString(
          fk?.fromAttributeId ?? fk?.sourceAttributeId ?? fk?.source?.attributeId ?? fk?.childAttributeId ?? fk?.columnId ?? fk?.childColumnId ?? fk?.from?.attributeId
        )
        const toAttributeId = toOptionalString(
          fk?.toAttributeId ?? fk?.targetAttributeId ?? fk?.target?.attributeId ?? fk?.parentAttributeId ?? fk?.referenceColumnId ?? fk?.parentColumnId ?? fk?.to?.attributeId
        )

        if (!fromEntityId && fromAttributeId) {
          fromEntityId = attributeOwnerMap.get(fromAttributeId)
        }
        if (!toEntityId && toAttributeId) {
          toEntityId = attributeOwnerMap.get(toAttributeId)
        }

        if (!fromEntityId || !toEntityId) {
          return null
        }

        return {
          id: toOptionalString(fk?.id ?? fk?.uuid ?? fk?.key ?? fk?.identifier ?? fkIndex, `fk-${fkIndex}`),
          fromEntityId,
          toEntityId,
          fromAttributeId,
          toAttributeId,
          relationshipType: fk?.relationshipType ?? fk?.type ?? fk?.cardinality ?? fk?.joinType,
          label: fk?.label ?? fk?.name ?? fk?.description
        }
      })
      .filter((fk): fk is DataModelingForeignKeyRecord => fk !== null)

    const relationshipMap = new Map<string, DataModelingRelationshipRecord>()
    normalizedRelationships.forEach(rel => {
      const key = `${rel.fromEntityId ?? ''}|${rel.toEntityId ?? ''}|${rel.type ?? ''}|${rel.label ?? ''}`
      if (!relationshipMap.has(key)) {
        relationshipMap.set(key, rel)
      }
    })
    const dedupedRelationships = Array.from(relationshipMap.values())

    const foreignKeyMap = new Map<string, DataModelingForeignKeyRecord>()
    normalizedForeignKeys.forEach(fk => {
      const key = `${fk.fromEntityId ?? ''}|${fk.toEntityId ?? ''}|${fk.fromAttributeId ?? ''}|${fk.toAttributeId ?? ''}`
      if (!foreignKeyMap.has(key)) {
        foreignKeyMap.set(key, fk)
      }
    })
    const dedupedForeignKeys = Array.from(foreignKeyMap.values())

    const normalizedComments: DataModelingCommentRecord[] = commentArray.map((comment: any, commentIndex: number) => ({
      id: toOptionalString(comment?.id ?? comment?.uuid ?? comment?.key ?? comment?.identifier ?? commentIndex, `comment-${commentIndex}`),
      text: comment?.text ?? comment?.content ?? comment?.note ?? comment?.description ?? '',
      color: comment?.color ?? comment?.tint,
      createdAt: comment?.createdAt ?? comment?.created_at ?? comment?.timestamp ?? comment?.lastUpdated,
      created_at: comment?.created_at ?? comment?.createdAt ?? comment?.timestamp,
      x: parseNumber(comment?.x ?? comment?.position?.x ?? comment?.left ?? comment?.layout?.x),
      y: parseNumber(comment?.y ?? comment?.position?.y ?? comment?.top ?? comment?.layout?.y),
      width: parseNumber(comment?.width ?? comment?.size?.width ?? comment?.layout?.width),
      height: parseNumber(comment?.height ?? comment?.size?.height ?? comment?.layout?.height)
    }))

    const rawZoomPan = parseJsonField(
      record.zoom_pan_state ?? record.zoomPanState ?? rawEntities?.zoomPan ?? rawEntities?.viewport ?? record.zoomPan
    ) || {}

    const normalizedZoom = parseNumber(rawZoomPan?.zoom ?? rawZoomPan?.scale)
    const normalizedPanX = parseNumber(rawZoomPan?.panX ?? rawZoomPan?.x ?? rawZoomPan?.offsetX)
    const normalizedPanY = parseNumber(rawZoomPan?.panY ?? rawZoomPan?.y ?? rawZoomPan?.offsetY)

    const zoomPan = (normalizedZoom !== undefined || normalizedPanX !== undefined || normalizedPanY !== undefined)
      ? {
          zoom: normalizedZoom,
          panX: normalizedPanX,
          panY: normalizedPanY
        }
      : null

    const savedAt = record.saved_at
      || record.last_save_at
      || record.updated_at
      || record.lastSaveAt
      || null

    const completionSeconds = parseNumber(record.completion_time_seconds ?? record.completionTimeSeconds)
    const wordCount = parseNumber(record.word_count ?? record.wordCount)

    return {
      id: record.id,
      uuid: record.uuid,
      version: record.version_number ?? record.versionNumber,
      version_number: record.version_number,
      content: record.content,
      entities: normalizedEntities,
      relationships: dedupedRelationships,
      foreignKeys: dedupedForeignKeys,
      comments: normalizedComments,
      zoomPan,
      savedAt,
      isCompleted: parseBoolean(record.is_completed ?? record.isCompleted, false),
      completionSeconds: completionSeconds ?? null,
      saveType: record.save_type ?? record.saveType,
      wordCount: wordCount ?? null
    }
  }

const mapWhiteboardRecord = (record: any, fallbackIndex = 0): WhiteboardEntry => {
    const rawResponse = parseJsonField(
      record.whiteboard_response ??
      record.whiteboardResponse ??
      record.response
    )

    const rawData = parseJsonField(
      record.whiteboard_data ??
      record.whiteboardData ??
      rawResponse
    )

    const payload = (
      rawResponse && typeof rawResponse === 'object' && !Array.isArray(rawResponse)
    ) ? rawResponse as Record<string, any> : (
      rawData && typeof rawData === 'object' && !Array.isArray(rawData)
    ) ? rawData as Record<string, any> : null

    const canvasElements = payload?.canvas_elements ?? payload?.canvasElements ?? record.canvas_elements ?? record.canvasElements
    const strokeData = payload?.stroke_data ?? payload?.strokeData ?? record.stroke_data ?? record.strokeData

    const contentText = toOptionalString(
      payload?.content ??
      record.content ??
      record.whiteboard_notes ??
      record.whiteboardNotes
    ) || ''

    const discussion = toOptionalString(
      record.whiteboard_notes ??
      record.whiteboardNotes ??
      payload?.whiteboard_notes ??
      payload?.whiteboardNotes
    ) || null

    const savedAtSource = (
      record.saved_at ??
      record.last_updated_at ??
      record.lastUpdatedAt ??
      record.updated_at ??
      payload?.saved_at ??
      payload?.savedAt
    )

    const normalizedSavedAt = (() => {
      if (!savedAtSource) return undefined
      if (typeof savedAtSource === 'string') return savedAtSource
      if (savedAtSource instanceof Date) return savedAtSource.toISOString()
      const parsed = new Date(savedAtSource)
      return Number.isNaN(parsed.getTime()) ? String(savedAtSource) : parsed.toISOString()
    })()

    const versionValue = (
      record.version ??
      record.version_number ??
      record.versionNumber ??
      payload?.version ??
      payload?.version_number ??
      payload?.versionNumber
    )

    const entryId =
      toOptionalString(record.uuid) ||
      toOptionalString(record.id) ||
      toOptionalString(payload?.uuid) ||
      (normalizedSavedAt ? `whiteboard-${normalizedSavedAt}` : null) ||
      (versionValue !== null && versionValue !== undefined ? `whiteboard-v${versionValue}` : null) ||
      `whiteboard-entry-${fallbackIndex}`

    return {
      id: entryId,
      uuid: toOptionalString(record.uuid),
      saved_at: normalizedSavedAt,
      savedAt: normalizedSavedAt,
      version: versionValue ? Number(versionValue) : null,
      version_number: versionValue ? Number(versionValue) : null,
      versionNumber: versionValue ? Number(versionValue) : null,
      input_type: toOptionalString(record.input_type ?? record.inputType ?? payload?.input_type ?? payload?.inputType) || 'whiteboard',
      input_category: toOptionalString(record.input_category ?? record.inputCategory ?? payload?.input_category ?? payload?.inputCategory) || 'whiteboard',
      action_type: toOptionalString(record.action_type ?? record.actionType ?? payload?.action_type ?? payload?.actionType) || 'auto',
      content: contentText,
      canvas_elements: canvasElements,
      canvasElements: canvasElements,
      whiteboard_text_discussion: discussion,
      whiteboardTextDiscussion: discussion,
      stroke_data: strokeData,
      strokeData: strokeData
    }
  }

  const mapUmlDesignRecord = (record: any): UmlDesignEntry => {
    // First, check if this is from interviews_responses table which has uml_design_response column
    let umlData = record
    
    // If the record has uml_design_response field, parse it
    if (record.uml_design_response) {
      try {
        umlData = JSON.parse(record.uml_design_response)
      } catch (e) {
        console.warn('Failed to parse uml_design_response:', e)
      }
    }
    
    const rawClasses = parseJsonField(umlData.classes_data ?? umlData.classesData ?? umlData.classes ?? record.classes_data ?? record.classesData ?? record.classes)
    const rawRelationships = parseJsonField(umlData.relationships_data ?? umlData.relationshipsData ?? umlData.relationships ?? record.relationships_data ?? record.relationshipsData ?? record.relationships)

    const classesRawArray = pickArray(
      rawClasses,
      rawClasses?.classes,
      rawClasses?.items,
      record.classes,
      record.classesData
    )

    const relationshipsRawArray = pickArray(
      rawRelationships,
      rawRelationships?.relationships,
      rawRelationships?.links,
      record.relationships,
      record.relationshipsData
    )

    const normalizedClasses: UmlClassRecord[] = classesRawArray.map((cls: any, index: number) => {
      const classId = toOptionalString(
        cls?.id ?? cls?.uuid ?? cls?.key ?? cls?.identifier ?? cls?.classId,
        `class-${index}`
      )!

      const attrArray = pickArray(
        cls?.attributes,
        cls?.fields,
        cls?.properties
      )

      const methodArray = pickArray(
        cls?.methods,
        cls?.operations,
        cls?.functions
      )

      const normalizedAttributes: UmlAttributeRecord[] = attrArray.map((attr: any, attrIndex: number) => ({
        id: toOptionalString(attr?.id ?? attr?.uuid ?? attr?.key ?? attr?.name ?? attrIndex, `${classId}-attr-${attrIndex}`)!,
        name: attr?.name ?? attr?.label ?? attr?.property ?? `attribute_${attrIndex + 1}`,
        type: attr?.type ?? attr?.dataType ?? attr?.fieldType ?? 'string',
        visibility: (attr?.visibility || attr?.access || attr?.modifier || 'public').toLowerCase()
      }))

      const normalizedMethods: UmlMethodRecord[] = methodArray.map((method: any, methodIndex: number) => ({
        id: toOptionalString(method?.id ?? method?.uuid ?? method?.name ?? methodIndex, `${classId}-method-${methodIndex}`)!,
        name: method?.name ?? method?.label ?? `method_${methodIndex + 1}`,
        returnType: method?.returnType ?? method?.returns ?? 'void',
        parameters: method?.parameters ?? method?.params ?? method?.arguments ?? '',
        visibility: (method?.visibility || method?.access || method?.modifier || 'public').toLowerCase()
      }))

      const computedWidth = Math.max(240, parseNumber(cls?.width) ?? 240)
      const computedHeight = Math.max(
        160,
        parseNumber(cls?.height) ?? (120 + normalizedAttributes.length * 24 + normalizedMethods.length * 24)
      )

      const defaultX = 80 + (index % 4) * 260
      const defaultY = 80 + Math.floor(index / 4) * 220

      return {
        id: classId,
        name: cls?.name ?? cls?.title ?? `Class ${index + 1}`,
        type: (cls?.type ?? cls?.classType ?? 'class').toLowerCase(),
        x: parseNumber(cls?.x ?? cls?.position?.x ?? cls?.left) ?? defaultX,
        y: parseNumber(cls?.y ?? cls?.position?.y ?? cls?.top) ?? defaultY,
        width: computedWidth,
        height: computedHeight,
        attributes: normalizedAttributes,
        methods: normalizedMethods
      }
    })

    const normalizedRelationships: UmlRelationshipRecord[] = []

    relationshipsRawArray.forEach((rel: any, relIndex: number) => {
      const fromId = toOptionalString(
        rel?.fromClassId ?? rel?.sourceClassId ?? rel?.source?.classId ?? rel?.from ?? rel?.sourceId
      )
      const toId = toOptionalString(
        rel?.toClassId ?? rel?.targetClassId ?? rel?.target?.classId ?? rel?.to ?? rel?.targetId
      )

      if (fromId && toId) {
        normalizedRelationships.push({
          id: toOptionalString(rel?.id ?? rel?.uuid ?? rel?.key ?? relIndex, `rel-${relIndex}`)!,
          fromClassId: fromId,
          toClassId: toId,
          type: (rel?.type ?? rel?.relationshipType ?? 'association').toLowerCase(),
          label: rel?.label ?? rel?.name ?? rel?.description
        })
      }
    })

    const savedAt = record.saved_at
      || record.last_save_at
      || record.updated_at
      || record.lastSaveAt
      || null

    const completionSeconds = parseNumber(record.completion_time_seconds ?? record.completionTimeSeconds)

    return {
      id: record.id ?? umlData.id,
      uuid: record.uuid ?? umlData.uuid,
      version_number: record.version_number ?? umlData.version_number,
      version: record.version_number ?? record.version ?? umlData.version_number ?? umlData.version,
      diagram_type: umlData.diagram_type ?? umlData.diagramType ?? record.diagram_type ?? record.diagramType ?? record.type,
      content: umlData.content ?? record.content ?? record.description,
      classes: normalizedClasses,
      relationships: normalizedRelationships,
      is_completed: parseBoolean(record.is_completed ?? record.isCompleted ?? umlData.is_completed ?? umlData.isCompleted, false),
      completion_time_seconds: completionSeconds ?? null,
      completionSeconds: completionSeconds ?? null,
      saved_at: savedAt,
      savedAt
    }
  }

  const mapSystemDesignRecord = (record: any): SystemDesignEntry => {
    // Handle interviews_responses format where content contains JSON with diagram_elements
    let diagramElementsData = null
    let contentText = ''
    let designNotesText = ''
    
    if (record.source === 'interviews_responses' && record.content) {
      try {
        const contentJson = JSON.parse(record.content)
        diagramElementsData = contentJson.diagram_elements
        contentText = contentJson.content || ''
        designNotesText = contentJson.design_notes || record.design_notes || ''
      } catch (e) {
        console.warn('Failed to parse content JSON:', e)
      }
    }
    
    // Also try whiteboard_data if no diagram elements found
    if (!diagramElementsData && record.whiteboard_data) {
      try {
        if (typeof record.whiteboard_data === 'string') {
          diagramElementsData = JSON.parse(record.whiteboard_data)
        } else if (typeof record.whiteboard_data === 'object') {
          diagramElementsData = record.whiteboard_data
        }
      } catch (e) {
        console.warn('Failed to parse whiteboard_data:', e)
      }
    }
    
    // Fallback to original logic for other sources
    const rawParsed = diagramElementsData || parseJsonField(record.diagram_elements ?? record.diagramElements ?? record.diagram)
    const parsedDiagram = rawParsed && rawParsed.diagramElements ? rawParsed.diagramElements : rawParsed

    const placedComponentsRaw = pickArray(
      parsedDiagram?.placedComponents,
      parsedDiagram?.components,
      parsedDiagram?.nodes,
      Array.isArray(parsedDiagram) ? parsedDiagram : undefined,
      record.placedComponents,
      record.components
    )

    const connectionsRaw = pickArray(
      parsedDiagram?.connections,
      parsedDiagram?.edges,
      parsedDiagram?.links,
      Array.isArray(parsedDiagram?.connections) ? parsedDiagram?.connections : undefined,
      Array.isArray(parsedDiagram) ? parsedDiagram.filter((item: any) => item && (item.fromComponentId || item.toComponentId)) : undefined,
      record.connections
    )

    const normalizedComponents: SystemDesignComponentRecord[] = placedComponentsRaw.map((component: any, index: number) => {
      const componentId = toOptionalString(
        component?.id ?? component?.uuid ?? component?.key ?? component?.identifier ?? `component-${index}`
      )!

      const defaultWidth = 180
      const defaultHeight = 120
      const width = parseNumber(component?.width) ?? defaultWidth
      const height = parseNumber(component?.height) ?? defaultHeight
      const defaultX = 100 + (index % 4) * 220
      const defaultY = 100 + Math.floor(index / 4) * 180

      return {
        id: componentId,
        componentId: toOptionalString(component?.componentId ?? component?.type ?? component?.templateId ?? componentId)!,
        name: component?.name ?? component?.title ?? component?.label ?? `Component ${index + 1}`,
        color: component?.color ?? component?.accent ?? component?.theme ?? '#2563eb',
        x: parseNumber(component?.x ?? component?.position?.x ?? component?.left) ?? defaultX,
        y: parseNumber(component?.y ?? component?.position?.y ?? component?.top) ?? defaultY,
        width,
        height
      }
    })

    const componentIdSet = new Set(normalizedComponents.map(component => component.id))

    const normalizedConnections: SystemDesignConnectionRecord[] = []

    connectionsRaw.forEach((connection: any, index: number) => {
      const fromId = toOptionalString(
        connection?.fromComponentId ?? connection?.sourceComponentId ?? connection?.source?.componentId ?? connection?.from ?? connection?.sourceId
      )
      const toId = toOptionalString(
        connection?.toComponentId ?? connection?.targetComponentId ?? connection?.target?.componentId ?? connection?.to ?? connection?.targetId
      )

      if (fromId && toId) {
        const connectionRecord = {
          id: toOptionalString(connection?.id ?? connection?.uuid ?? connection?.key ?? index, `connection-${index}`)!,
          fromComponentId: fromId,
          toComponentId: toId,
          color: connection?.color ?? connection?.stroke ?? '#374151',
          strokeWidth: parseNumber(connection?.strokeWidth ?? connection?.width ?? connection?.thickness) ?? 2,
          type: (connection?.type ?? connection?.style ?? 'arrow').toLowerCase(),
          label: toOptionalString(connection?.label ?? connection?.name)
        }

        const isValid = componentIdSet.has(connectionRecord.fromComponentId) && componentIdSet.has(connectionRecord.toComponentId)
        if (isValid) {
          normalizedConnections.push(connectionRecord)
        } else {
          console.log('❌ Connection filtered out:', {
            connection: connectionRecord,
            hasFrom: componentIdSet.has(connectionRecord.fromComponentId),
            hasTo: componentIdSet.has(connectionRecord.toComponentId),
            componentIds: Array.from(componentIdSet)
          })
        }
      }
    })

    const savedAt = record.saved_at
      || record.last_save_at
      || record.updated_at
      || record.lastSaveAt
      || null

    const rawCanvasSize = parsedDiagram?.canvasSize ?? parsedDiagram?.size ?? parsedDiagram?.dimensions
    const canvasSize = rawCanvasSize
      ? {
          width: parseNumber(rawCanvasSize.width) ?? undefined,
          height: parseNumber(rawCanvasSize.height) ?? undefined
        }
      : undefined

    const rawViewport = parsedDiagram?.viewport ?? parsedDiagram?.view ?? parsedDiagram?.pan
    const viewport = rawViewport
      ? {
          zoom: parseNumber(rawViewport.zoom ?? rawViewport.scale) ?? 1,
          panX: parseNumber(rawViewport.panX ?? rawViewport.x ?? rawViewport.offsetX) ?? 0,
          panY: parseNumber(rawViewport.panY ?? rawViewport.y ?? rawViewport.offsetY) ?? 0
        }
      : undefined

    return {
      id: record.id,
      uuid: record.uuid,
      version: record.version ?? record.version_number,
      savedAt,
      content: contentText || record.text_data || '',
      diagramElements: {
        placedComponents: normalizedComponents,
        connections: normalizedConnections,
        canvasSize,
        viewport
      },
      designNotes: designNotesText || record.design_notes || record.designNotes || record.system_design_discussion,
      discussion: record.system_design_discussion || record.discussion
    }
  }

  const loadDataModelingEntries = async (interviewUuid: string) => {
    try {
      setDataModelingLoading(true)
      setDataModelingError(null)

      const apiResponse = await apiClient.getInterviewDataModeling(interviewUuid)

      if (apiResponse.success && (apiResponse.data as any)?.response) {
        const dataPayload = apiResponse.data as any
        const responseRecord = {
          ...dataPayload.response,
          entities_data: dataPayload.entities ?? dataPayload.response?.entities_data,
          entitiesData: dataPayload.entities ?? dataPayload.response?.entitiesData,
          database_modeling_entities: dataPayload.entities ?? dataPayload.response?.database_modeling_entities,
          foreign_keys_data: dataPayload.foreign_keys ?? dataPayload.response?.foreign_keys_data,
          comments_data: dataPayload.comments ?? dataPayload.response?.comments_data,
          database_modeling_foreign_keys: dataPayload.foreign_keys ?? dataPayload.response?.database_modeling_foreign_keys,
          database_modeling_comments: dataPayload.comments ?? dataPayload.response?.database_modeling_comments,
          relationships_data: dataPayload.relationships ?? dataPayload.response?.relationships_data,
          database_modeling_data: dataPayload.modeling_data ?? dataPayload.response?.database_modeling_data,
          model_data: dataPayload.modeling_data ?? dataPayload.response?.model_data,
          modeling_data: dataPayload.modeling_data ?? dataPayload.response?.modeling_data,
          database_modeling_zoom_pan: dataPayload.zoom_pan ?? dataPayload.response?.database_modeling_zoom_pan,
          zoom_pan: dataPayload.zoom_pan ?? dataPayload.response?.zoom_pan
        }
        const entry = mapDataModelingRecord(responseRecord)
        setDataModelingEntries(entry ? [entry] : [])
      } else {
        setDataModelingEntries([])
        if (apiResponse.error) {
          setDataModelingError(apiResponse.error)
        }
      }
    } catch (err) {
      console.error('Failed to load data modeling responses:', err)
      setDataModelingError('Failed to load data modeling responses')
    } finally {
      setDataModelingLoading(false)
    }
  }

  const loadUmlDesignEntries = async (interviewUuid: string) => {
    try {
      setUmlDesignLoading(true)
      setUmlDesignError(null)

      let apiResponse = await apiClient.getInterviewUmlDesign(interviewUuid)
      if (!apiResponse.success || !(apiResponse.data as any)?.response) {
        apiResponse = await apiClient.getPublicInterviewResponse('uml_design', interviewUuid)
      }

      if (apiResponse.success && (apiResponse.data as any)?.response) {
        let responseRecord = (apiResponse.data as any).response
        // Normalize payload when public endpoint returns plain diagram object
        if (responseRecord && !responseRecord.uml_design_data && !responseRecord.classes) {
          responseRecord = {
            ...responseRecord,
            uml_design_data: responseRecord,
            source: 'interviews_responses'
          }
        }

        const entry = mapUmlDesignRecord(responseRecord)
        setUmlDesignEntries(entry ? [entry] : [])
      } else {
        setUmlDesignEntries([])
        if (apiResponse.error) {
          setUmlDesignError(apiResponse.error)
        }
      }
    } catch (err) {
      console.error('Failed to load UML design responses:', err)
      setUmlDesignError('Failed to load UML design responses')
    } finally {
      setUmlDesignLoading(false)
    }
  }

  const loadSystemDesignEntries = async (interviewUuid: string) => {
    try {
      setSystemDesignLoading(true)
      setSystemDesignError(null)

      let apiResponse = await apiClient.getInterviewSystemDesign(interviewUuid)
      if (!apiResponse.success || !(apiResponse.data as any)?.response) {
        apiResponse = await apiClient.getPublicInterviewResponse('system_design', interviewUuid)
      }

      if (apiResponse.success && (apiResponse.data as any)?.response) {
        const dataPayload = apiResponse.data as any
        let responseRecord: any = {
          ...dataPayload.response,
          whiteboard_data: dataPayload.whiteboard_data ?? dataPayload.response?.whiteboard_data,
          system_design_whiteboard_data: dataPayload.whiteboard_data ?? dataPayload.response?.system_design_whiteboard_data,
          systemDesignWhiteboardData: dataPayload.whiteboard_data ?? dataPayload.response?.systemDesignWhiteboardData
        }

        // Normalize when public endpoint returns plain diagram object
        if (!responseRecord.content && !responseRecord.diagram_elements && typeof dataPayload.response === 'object') {
          responseRecord = {
            ...responseRecord,
            source: 'interviews_responses',
            content: JSON.stringify(dataPayload.response)
          }
        }
        const entry = mapSystemDesignRecord(responseRecord)
        setSystemDesignEntries(entry ? [entry] : [])
      } else {
        setSystemDesignEntries([])
        if (apiResponse.error) {
          setSystemDesignError(apiResponse.error)
        }
      }
    } catch (err) {
      console.error('Failed to load system design responses:', err)
      setSystemDesignError('Failed to load system design responses')
    } finally {
      setSystemDesignLoading(false)
    }
  }

  const loadWhiteboardEntries = async (interviewUuid: string) => {
    try {
      setWhiteboardLoading(true)
      setWhiteboardError(null)

      let apiResponse = await apiClient.getInterviewWhiteboard(interviewUuid)
      if (!apiResponse.success || !(apiResponse.data as any)?.response) {
        apiResponse = await apiClient.getPublicInterviewResponse('whiteboard', interviewUuid)
      }

      if (apiResponse.success && (apiResponse.data as any)?.response) {
        let responseRecord = (apiResponse.data as any).response

        // Normalize when public endpoint returns plain whiteboard object
        if (responseRecord && !responseRecord.whiteboard_response && typeof responseRecord === 'object') {
          responseRecord = {
            ...responseRecord,
            whiteboard_response: JSON.stringify(responseRecord),
            source: 'interviews_responses'
          }
        }

        const entry = mapWhiteboardRecord(responseRecord, 0)
        setWhiteboardEntries(entry ? [entry] : [])
      } else {
        setWhiteboardEntries([])
        if (apiResponse.error) {
          setWhiteboardError(apiResponse.error)
        }
      }
    } catch (err) {
      console.error('Failed to load whiteboard responses:', err)
      setWhiteboardError('Failed to load whiteboard responses')
    } finally {
      setWhiteboardLoading(false)
    }
  }

  useEffect(() => {
    if (!response) return

    if (response.database_challenges && response.database_challenges.length > 0) {
      setDatabaseChallengesError(null)
      setDatabaseChallenges(response.database_challenges)
      return
    }

    if (response.id) {
      loadDatabaseChallenges(response.id)
    } else {
      setDatabaseChallenges([])
    }
  }, [response])

  const loadVisualResponses = async (interviewUuid: string) => {
    try {
      setSystemDesignLoading(true)
      setUmlDesignLoading(true)
      setDataModelingLoading(true)
      setWhiteboardLoading(true)

      setSystemDesignError(null)
      setUmlDesignError(null)
      setDataModelingError(null)
      setWhiteboardError(null)

      const apiResponse = await apiClient.get(`/interviews/${interviewUuid}/visual-responses`)

      if (apiResponse.success && apiResponse.data) {
        const payload = apiResponse.data as any

        // System design
        const systemDesignPayload = payload.system_design
        if (systemDesignPayload?.response) {
          const responseRecord = {
            ...systemDesignPayload.response,
            whiteboard_data: systemDesignPayload.whiteboard_data ?? systemDesignPayload.response?.whiteboard_data,
            system_design_whiteboard_data: systemDesignPayload.response?.system_design_whiteboard_data,
            systemDesignWhiteboardData: systemDesignPayload.response?.systemDesignWhiteboardData
          }
          const entry = mapSystemDesignRecord(responseRecord)
          setSystemDesignEntries(entry ? [entry] : [])
        } else {
          setSystemDesignEntries([])
        }

        // UML
        const umlPayload = payload.uml_design
        if (umlPayload?.response) {
          const entry = mapUmlDesignRecord(umlPayload.response)
          setUmlDesignEntries(entry ? [entry] : [])
        } else {
          setUmlDesignEntries([])
        }

        // Data modeling
        const dataModelingPayload = payload.data_modeling
        if (dataModelingPayload?.response) {
          const responseRecord = {
            ...dataModelingPayload.response,
            entities_data: dataModelingPayload.entities,
            entitiesData: dataModelingPayload.entities,
            database_modeling_entities: dataModelingPayload.entities,
            foreign_keys_data: dataModelingPayload.foreign_keys,
            comments_data: dataModelingPayload.comments,
            database_modeling_foreign_keys: dataModelingPayload.foreign_keys,
            database_modeling_comments: dataModelingPayload.comments,
            relationships_data: dataModelingPayload.relationships,
            database_modeling_data: dataModelingPayload.modeling_data,
            model_data: dataModelingPayload.modeling_data,
            modeling_data: dataModelingPayload.modeling_data,
            database_modeling_zoom_pan: dataModelingPayload.zoom_pan,
            zoom_pan: dataModelingPayload.zoom_pan
          }
          const entry = mapDataModelingRecord(responseRecord)
          setDataModelingEntries(entry ? [entry] : [])
        } else {
          setDataModelingEntries([])
        }

        // Whiteboard
        const whiteboardPayload = payload.whiteboard
        if (whiteboardPayload?.response) {
          const entry = mapWhiteboardRecord(whiteboardPayload.response, 0)
          setWhiteboardEntries(entry ? [entry] : [])
        } else {
          setWhiteboardEntries([])
        }
      } else {
        setSystemDesignEntries([])
        setUmlDesignEntries([])
        setDataModelingEntries([])
        setWhiteboardEntries([])
      }
    } catch (err) {
      console.error('Failed to load visual responses:', err)
      setSystemDesignError('Failed to load system design responses')
      setUmlDesignError('Failed to load UML design responses')
      setDataModelingError('Failed to load data modeling responses')
      setWhiteboardError('Failed to load whiteboard responses')
      setSystemDesignEntries([])
      setUmlDesignEntries([])
      setDataModelingEntries([])
      setWhiteboardEntries([])
    } finally {
      setSystemDesignLoading(false)
      setUmlDesignLoading(false)
      setDataModelingLoading(false)
      setWhiteboardLoading(false)
    }
  }

  useEffect(() => {
    if (!response) return
    const targetId = (response as any)?.uuid || response.id || interviewId
    if (targetId) {
      loadVisualResponses(targetId)
    }
  }, [response, interviewId])

  useEffect(() => {
    if (!interviewId) return
    loadVisualResponses(interviewId)
  }, [interviewId])

  const candidateInputsByCategory = useMemo(() => {
    const groups = {
      coding: [] as CandidateInput[],
      database: [] as CandidateInput[],
      dataModeling: [] as CandidateInput[],
      systemDesign: [] as CandidateInput[],
      uml: [] as CandidateInput[],
      whiteboard: [] as CandidateInput[],
      notes: [] as CandidateInput[],
      qa: [] as CandidateInput[],
      other: [] as CandidateInput[]
    }

    if (!response?.candidate_inputs) {
      return groups
    }

    response.candidate_inputs.forEach(input => {
      const type = (input.type || '').toLowerCase()
      const category = (input.category || '').toLowerCase()

      if (type === 'code' || category === 'coding_solution') {
        groups.coding.push(input)
      }

      if (type === 'database' || category === 'database_challenge') {
        groups.database.push(input)
      }

      if (
        type === 'data_modeling' ||
        type === 'data-modeling' ||
        category === 'data_modeling' ||
        category === 'data-modeling' ||
        category === 'database_modeling' ||
        category === 'database-modeling'
      ) {
        groups.dataModeling.push(input)
      }

      if (type === 'system_design' || category === 'design' || category === 'system_design') {
        groups.systemDesign.push(input)
      }

      if (type === 'uml_design' || category === 'uml' || category === 'uml_design') {
        groups.uml.push(input)
      }

      if (type === 'whiteboard' || category === 'whiteboard') {
        groups.whiteboard.push(input)
      }

      if (type === 'notes' || category === 'notes' || category === 'note' || category === 'notepad') {
        groups.notes.push(input)
      }

      if (category === 'qa' || type === 'question' || type === 'answer') {
        groups.qa.push(input)
      }

      if (
        !['code', 'database', 'data_modeling', 'system_design', 'uml_design', 'whiteboard'].includes(type) &&
        ![
          'coding_solution',
          'database_challenge',
          'data_modeling',
          'database_modeling',
          'design',
          'system_design',
          'uml',
          'uml_design',
          'whiteboard',
          'qa'
        ].includes(category)
      ) {
        groups.other.push(input)
      }
    })

    return groups
  }, [response?.candidate_inputs])

  const workSectionSummary = useMemo(() => {
    const codingProblems = response?.problems || []
    const codingAssigned = codingProblems.length
    const codingSubmissions = codingProblems.reduce((total, problem) => total + (problem.submissions ? problem.submissions.length : 0), 0)

    const dataModelingCount = dataModelingEntries.length > 0
      ? dataModelingEntries.length
      : candidateInputsByCategory.dataModeling.length

    const databaseChallengeCount = databaseChallenges.length > 0
      ? databaseChallenges.length
      : candidateInputsByCategory.database.length

    const systemDesignCount = systemDesignEntries.length > 0
      ? systemDesignEntries.length
      : candidateInputsByCategory.systemDesign.length

    const umlCount = umlDesignEntries.length > 0
      ? umlDesignEntries.length
      : candidateInputsByCategory.uml.length

    const whiteboardCount = whiteboardEntries.length > 0
      ? whiteboardEntries.length
      : response?.whiteboard_data && response.whiteboard_data.length > 0
      ? response.whiteboard_data.length
      : candidateInputsByCategory.whiteboard.length

    return {
      codingAssigned,
      codingSubmissions,
      dataModelingCount,
      databaseChallengeCount,
      systemDesignCount,
      umlCount,
      whiteboardCount
    }
  }, [response?.problems, response?.whiteboard_data, databaseChallenges, dataModelingEntries, systemDesignEntries, umlDesignEntries, whiteboardEntries, candidateInputsByCategory])

  const workSummaryCards = useMemo(() => [
    {
      key: 'coding',
      label: 'Coding',
      icon: Code2,
      iconColor: 'text-blue-600',
      highlight: workSectionSummary.codingSubmissions > 0
        ? `${workSectionSummary.codingSubmissions} submission${workSectionSummary.codingSubmissions === 1 ? '' : 's'}`
        : 'No submissions yet',
      description: workSectionSummary.codingAssigned > 0
        ? `${workSectionSummary.codingAssigned} problem${workSectionSummary.codingAssigned === 1 ? '' : 's'} assigned`
        : 'No problems assigned'
    },
    {
      key: 'databaseModeling',
      label: 'Database Modeling',
      icon: Database,
      iconColor: 'text-emerald-600',
      highlight: workSectionSummary.dataModelingCount > 0
        ? `${workSectionSummary.dataModelingCount} artifact${workSectionSummary.dataModelingCount === 1 ? '' : 's'}`
        : 'No artifacts yet',
      description: workSectionSummary.dataModelingCount > 0
        ? 'Review entity relationships'
        : 'Awaiting candidate submission'
    },
    {
      key: 'databaseChallenge',
      label: 'Database Challenge',
      icon: Zap,
      iconColor: 'text-purple-600',
      highlight: workSectionSummary.databaseChallengeCount > 0
        ? `${workSectionSummary.databaseChallengeCount} record${workSectionSummary.databaseChallengeCount === 1 ? '' : 's'}`
        : 'No attempts yet',
      description: workSectionSummary.databaseChallengeCount > 0
        ? 'View SQL queries & results'
        : 'Candidate did not attempt the challenge'
    },
    {
      key: 'systemDesign',
      label: 'System Design',
      icon: Layers,
      iconColor: 'text-indigo-600',
      highlight: workSectionSummary.systemDesignCount > 0
        ? `${workSectionSummary.systemDesignCount} session${workSectionSummary.systemDesignCount === 1 ? '' : 's'}`
        : 'No sessions saved',
      description: workSectionSummary.systemDesignCount > 0
        ? 'Architecture diagrams available'
        : 'No system design artifacts'
    },
    {
      key: 'uml',
      label: 'UML Design',
      icon: Network,
      iconColor: 'text-pink-600',
      highlight: workSectionSummary.umlCount > 0
        ? `${workSectionSummary.umlCount} diagram${workSectionSummary.umlCount === 1 ? '' : 's'}`
        : 'No diagrams yet',
      description: workSectionSummary.umlCount > 0
        ? 'Class/sequence diagrams captured'
        : 'Candidate skipped UML stage'
    },
    {
      key: 'whiteboard',
      label: 'Whiteboard Notes',
      icon: Palette,
      iconColor: 'text-orange-500',
      highlight: workSectionSummary.whiteboardCount > 0
        ? `${workSectionSummary.whiteboardCount} board${workSectionSummary.whiteboardCount === 1 ? '' : 's'}`
        : 'No boards saved',
      description: workSectionSummary.whiteboardCount > 0
        ? 'Visual notes available'
        : 'No whiteboard activity recorded'
    }
  ], [workSectionSummary])

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600 bg-green-100'
    if (score >= 80) return 'text-blue-600 bg-blue-100'
    if (score >= 70) return 'text-yellow-600 bg-yellow-100'
    return 'text-red-600 bg-red-100'
  }

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strong_hire': return 'text-green-800 bg-green-100'
      case 'hire': return 'text-green-700 bg-green-50'
      case 'no_hire': return 'text-red-700 bg-red-50'
      case 'strong_no_hire': return 'text-red-800 bg-red-100'
      default: return 'text-gray-700 bg-gray-100'
    }
  }

  const formatRecommendation = (recommendation: string) => {
    return recommendation.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const toggleSolutionExpansion = (problemId: number) => {
    setExpandedSolutions(prev => ({
      ...prev,
      [problemId]: !prev[problemId]
    }))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading interview response...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-500 mt-1" />
          <div>
            <h3 className="text-lg font-medium text-red-800">Error Loading Interview</h3>
            <p className="text-red-700 mt-2">{error}</p>
            <button
              onClick={loadInterviewResponse}
              className="mt-4 text-red-600 hover:text-red-500 underline"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (!response) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Interview Not Found</h3>
          <p className="text-gray-600 mt-2">The interview response you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate('/recruiter/interviews')}
            className="mt-4 text-blue-600 hover:text-blue-500 underline"
          >
            Back to Interviews
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/recruiter/interviews')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center space-x-3">
              <h1 className="text-2xl font-bold text-gray-900">Interview Response</h1>
              {response.is_draft && (
                <span className="inline-flex px-3 py-1 text-sm font-semibold rounded-full bg-orange-100 text-orange-800 border border-orange-200">
                  Draft
                </span>
              )}
            </div>
            <p className="text-gray-600">{response.candidate?.name || 'Unknown Candidate'} - {response.position?.title || 'Unknown Position'}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          {response.status !== 'completed' && (
            <button 
              onClick={() => navigate(`/recruiter/interviews/${interviewId}/edit`)}
              className="btn-secondary"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </button>
          )}
          <button 
            className="btn-secondary"
            onClick={() => setShareModalOpen(true)}
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </button>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <User className="w-8 h-8 text-blue-600" />
            <div>
              <div className="text-sm text-gray-600">Status</div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  response.is_draft 
                    ? 'bg-orange-100 text-orange-800 border border-orange-200' 
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {response.is_draft ? 'draft' : response.status}
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <Clock className="w-8 h-8 text-green-600" />
            <div>
              <div className="text-sm text-gray-600">Duration</div>
              <div className="font-semibold text-gray-900">{response.duration_minutes} min</div>
            </div>
          </div>
        </div>
        
        {response.overall_rating && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-center space-x-3">
              <Star className="w-8 h-8 text-yellow-600" />
              <div>
                <div className="text-sm text-gray-600">Overall Score</div>
                <div className={`font-semibold px-2 py-1 rounded-full text-sm ${getScoreColor(response.overall_rating)}`}>
                  {response.overall_rating}/100
                </div>
              </div>
            </div>
          </div>
        )}
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-8 h-8 text-purple-600" />
            <div>
              <div className="text-sm text-gray-600">Recommendation</div>
              <div className={`font-semibold px-2 py-1 rounded-full text-xs ${getRecommendationColor(response.recommendation || 'pending')}`}>
                {formatRecommendation(response.recommendation || 'pending')}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'overview', label: 'Overview', icon: FileText },
              { id: 'coding', label: 'Coding', icon: Code2 },
              { id: 'database', label: 'Database Modeling', icon: Database },
              { id: 'databaseChallenge', label: 'Database Challenge', icon: Zap },
              { id: 'design', label: 'System Design', icon: Server },
              { id: 'uml', label: 'UML Design', icon: Network },
              { id: 'whiteboard', label: 'Whiteboard Notes', icon: Palette },
              { id: 'notes', label: 'Notes', icon: StickyNote },
              { id: 'responses', label: 'Q&A Responses', icon: MessageSquare },
              { id: 'recording', label: 'Recording', icon: Play }
            ].map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as any)
                    if (tab.id === 'notes') {
                      fetchInterviewNotes()
                    }
                  }}
                  className={`flex items-center space-x-2 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Interview Details</h3>
                  <dl className="space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Candidate</dt>
                      <dd className="text-sm text-gray-900">{response.candidate?.name || 'Unknown Candidate'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Email</dt>
                      <dd className="text-sm text-gray-900">{response.candidate?.email || 'No email provided'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Position</dt>
                      <dd className="text-sm text-gray-900">{response.position?.title || 'Unknown Position'}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Interview Date</dt>
                      <dd className="text-sm text-gray-900">
                        {new Date(response.scheduled_at).toLocaleString()}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Interview URL</dt>
                      <dd className="text-sm text-gray-900">
                        {response.session?.uuid && response.interview_type === 'technical' ? (
                          // Technical interview with generated session URL
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                              {`${window.location.origin}/interview/?session=${response.session?.uuid}&interviewId=${interviewId}&stage=technical`}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/interview/?session=${response.session?.uuid}&interviewId=${interviewId}&stage=technical`)}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              Copy
                            </button>
                          </div>
                        ) : response.meeting_url ? (
                          // Regular interview with meeting URL
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {response.meeting_url}
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(response.meeting_url || '')}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          // Fallback URL using interview UUID and session_id if available
                          <div className="flex items-center space-x-2">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {response.session?.uuid 
                                ? `${window.location.origin}/interview/?session=${response.session.uuid}&interviewId=${interviewId}&stage=technical`
                                : `${window.location.origin}/interview/?interviewId=${interviewId}&stage=technical`
                              }
                            </span>
                            <button
                              onClick={() => navigator.clipboard.writeText(
                                response.session?.uuid 
                                  ? `${window.location.origin}/interview/?session=${response.session.uuid}&interviewId=${interviewId}&stage=technical`
                                  : `${window.location.origin}/interview/?interviewId=${interviewId}&stage=technical`
                              )}
                              className="text-blue-600 hover:text-blue-800 text-xs"
                            >
                              Copy
                            </button>
                          </div>
                        )}
                        {response.interview_type === 'technical' && (
                          <p className="text-xs text-gray-500 mt-1">
                            {response.session?.uuid ? 'Share this URL with the candidate to access the coding challenge' : 'Technical interview URL will be generated when session is created'}
                          </p>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Performance Summary</h3>
                  <div className="space-y-3">
                    {response.problems && response.problems.length > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Coding Problems</span>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {response.problems.length} assigned
                        </span>
                      </div>
                    )}
                    {response.session && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Session Progress</span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${response.session.completion_percentage ? getScoreColor(response.session.completion_percentage) : 'bg-gray-100 text-gray-800'}`}>
                          {response.session.completion_percentage || 0}% complete
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Candidate Work Summary</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {workSummaryCards.map(card => {
                    const Icon = card.icon
                    return (
                      <div key={card.key} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center space-x-3 mb-2">
                          <Icon className={`w-5 h-5 ${card.iconColor}`} />
                          <span className="font-medium text-gray-900">{card.label}</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">{card.highlight}</p>
                        <p className="text-xs text-gray-500 mt-1">{card.description}</p>
                      </div>
                    )
                  })}
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Interviewer Notes</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{response.notes || 'No notes available'}</p>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {response.candidate?.skills && response.candidate.skills.length > 0 ? response.candidate.skills.map((skill: any, index: number) => {
                    const skillName = typeof skill === 'string' 
                      ? skill 
                      : (skill as any)?.skillName || (skill as any)?.skill_name || (skill as any)?.name;
                    return (
                      <span key={(skill as any)?.uuid || index} className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                        {skillName}
                      </span>
                    );
                  }) : (
                    <span className="text-gray-500 text-sm">No skills listed</span>
                  )}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Next Steps</h3>
                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-blue-800">{response.notes || 'No next steps specified'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Coding Challenge Tab */}
          {activeTab === 'coding' && response.problems && response.problems.length > 0 && (
            <div className="space-y-6">
              {response.problems.map((problem, index) => (
                <div key={problem.problem_id} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Problem {index + 1}: {problem.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      problem.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                      problem.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {problem.difficulty.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700">{problem.description}</p>
                  </div>

                  {/* Solution Section */}
                  {(problem.editorial_solution || problem.solution) && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-gray-900">Expected Solution</h4>
                        <button
                          onClick={() => toggleSolutionExpansion(problem.problem_id)}
                          className="flex items-center space-x-2 text-sm text-blue-600 hover:text-blue-800"
                        >
                          <span>{expandedSolutions[problem.problem_id] ? 'Collapse' : 'Expand'}</span>
                          {expandedSolutions[problem.problem_id] ? (
                            <ChevronUp className="w-4 h-4" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      
                      {expandedSolutions[problem.problem_id] && (
                        <>
                          {problem.editorial_solution && (
                            <div className="bg-blue-50 rounded-lg p-4 mb-3">
                              <h5 className="text-sm font-medium text-blue-900 mb-2">Editorial</h5>
                              <p className="text-blue-800 text-sm">{problem.editorial_solution}</p>
                            </div>
                          )}
                          
                          {problem.solution && Object.keys(problem.solution).length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-sm font-medium text-gray-700">Solution Code</h5>
                              {Object.entries(problem.solution).map(([language, code]) => (
                                <div key={language} className="border border-gray-200 rounded-lg">
                                  <div className="bg-gray-100 px-3 py-2 border-b border-gray-200">
                                    <span className="text-xs font-medium text-gray-600 uppercase">{language}</span>
                                  </div>
                                  <div className="bg-gray-900 p-3">
                                    <pre className="text-sm text-green-400 whitespace-pre-wrap overflow-x-auto">
                                      <code>{code}</code>
                                    </pre>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  
                  {problem.submissions && problem.submissions.length > 0 ? (
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Candidate Submissions ({problem.submissions.length})</h4>
                      {problem.submissions.map((submission, subIndex) => (
                        <div key={submission.id} className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-700">Submission {subIndex + 1}</span>
                            <div className="flex items-center space-x-2">
                              <span className="text-xs text-gray-500">{submission.language}</span>
                              {submission.status && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  submission.status === 'passed' ? 'bg-green-100 text-green-800' :
                                  submission.status === 'failed' ? 'bg-red-100 text-red-800' :
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {submission.status}
                                </span>
                              )}
                              {submission.score && (
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getScoreColor(submission.score)}`}>
                                  {submission.score}%
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Test Case Results */}
                          {(submission.passed_tests !== undefined && submission.total_tests !== undefined) && (
                            <div className="flex items-center space-x-4 mb-3">
                              <div className="flex items-center space-x-2">
                                <CheckCircle2 className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-gray-700">
                                  Test Cases: <span className="font-medium text-green-600">{submission.passed_tests}</span>
                                  /<span className="font-medium">{submission.total_tests}</span> passed
                                </span>
                              </div>
                              {submission.total_tests > 0 && (
                                <div className="flex-1 bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-green-500 h-2 rounded-full transition-all duration-300"
                                    style={{ width: `${(submission.passed_tests / submission.total_tests) * 100}%` }}
                                  ></div>
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="bg-gray-900 rounded-lg p-3 mb-2">
                            <pre className="text-sm text-green-400 whitespace-pre-wrap overflow-x-auto">
                              <code>{submission.code}</code>
                            </pre>
                          </div>

                          {/* Test Results Details */}
                          {submission.test_results && Array.isArray(submission.test_results) && (
                            <div className="mb-3">
                              <h6 className="text-sm font-medium text-gray-700 mb-2">Test Case Details</h6>
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {submission.test_results.map((test, testIndex) => (
                                  <div key={testIndex} className={`flex items-center justify-between p-2 rounded text-xs ${
                                    test.passed ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                  }`}>
                                    <div className="flex items-center space-x-2">
                                      {test.passed ? (
                                        <CheckCircle2 className="w-3 h-3" />
                                      ) : (
                                        <XCircle className="w-3 h-3" />
                                      )}
                                      <span>Test Case {testIndex + 1}</span>
                                    </div>
                                    <span className="font-medium">
                                      {test.passed ? 'PASSED' : 'FAILED'}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          
                          {submission.feedback && (
                            <div className="bg-blue-50 rounded-lg p-3 mb-2">
                              <p className="text-blue-800 text-sm">{submission.feedback}</p>
                            </div>
                          )}
                          
                          {(submission.execution_time || submission.memory_used || submission.submitted_at) && (
                            <div className="flex space-x-4 mt-2 text-xs text-gray-600">
                              {submission.execution_time && <span>Execution: {submission.execution_time}ms</span>}
                              {submission.memory_used && <span>Memory: {submission.memory_used}MB</span>}
                              {submission.submitted_at && <span>Submitted: {new Date(submission.submitted_at).toLocaleString()}</span>}
                            </div>
                          )}
                          
                          {/* Evaluation Display for this submission */}
                          <div className="mt-4 pt-4 border-t border-gray-200">
                          <EvaluationDisplay
                            sourceTable="code_submissions"
                            sourceRecordUuid={submission.id.toString()}
                            candidateUuid={response.candidate?.uuid || response.candidate?.id?.toString() || ''}
                            interviewUuid={response.id}
                            title={`${problem.title} - Submission ${subIndex + 1}`}
                          />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 rounded-lg p-4">
                      <p className="text-yellow-800">No submissions yet</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* System Design Tab */}
          {activeTab === 'design' && (
            <div className="space-y-6">
              {systemDesignError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Unable to load system design responses</p>
                    <p className="text-sm text-red-700">{systemDesignError}</p>
                  </div>
                </div>
              )}

              {systemDesignLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : systemDesignEntries.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">System Design Sessions ({systemDesignEntries.length})</h3>
                  {systemDesignEntries.map((design, index) => (
                    <div key={design.uuid || design.id || index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-purple-50 border-b border-purple-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Server className="w-5 h-5 text-purple-600" />
                            <div>
                              <h4 className="font-medium text-gray-900">
                                System Design Session {index + 1}{design.version ? ` - Version ${design.version}` : ''}
                              </h4>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {design.savedAt ? new Date(design.savedAt).toLocaleString() : 'No timestamp'}
                          </span>
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        <SystemDesignViewer
                          diagramElements={design.diagramElements}
                          content={design.content}
                          designNotes={design.designNotes}
                          systemDesignDiscussion={design.discussion}
                        />

                        <EvaluationForm
                          sourceTable="system_design_responses"
                          sourceRecordUuid={design.uuid || (design.id ? String(design.id) : '')}
                          candidateUuid={response.candidate.uuid || response.candidate.id?.toString() || ''}
                          interviewUuid={response.id}
                          title={`System Design Session ${index + 1}${design.version ? ` - Version ${design.version}` : ''}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : candidateInputsByCategory.systemDesign.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-blue-600" />
                        <h3 className="text-lg font-medium text-gray-900">System Design Notes</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-6">
                      {candidateInputsByCategory.systemDesign.map((input, index) => (
                        <div key={input.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="font-medium text-gray-900">{input.type || `Design Response ${index + 1}`}</h4>
                            <span className="text-xs text-gray-500">
                              {input.last_save_at ? new Date(input.last_save_at).toLocaleString() : 'No timestamp'}
                            </span>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-700 leading-relaxed">
                            {renderTextWithLinks(input.content)}
                          </div>
                          {input.file_url && (
                            <div className="mt-3">
                              <a href={input.file_url} target="_blank" rel="noopener noreferrer" 
                                 className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-800 text-sm">
                                <Download className="w-4 h-4" />
                                <span>View Attachment</span>
                              </a>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                    <Server className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No System Design Data</h3>
                    <p className="text-gray-600">The candidate didn't complete any system design tasks during this interview.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* UML Design Tab */}
          {activeTab === 'uml' && (
            <div className="space-y-6">
              {umlDesignError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Unable to load UML design responses</p>
                    <p className="text-sm text-red-700">{umlDesignError}</p>
                  </div>
                </div>
              )}

              {umlDesignLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : umlDesignEntries.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">UML Design Responses ({umlDesignEntries.length})</h3>
                  {umlDesignEntries.map((uml, index) => (
                    <div key={uml.uuid || uml.id || index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-purple-50 border-b border-purple-200 px-6 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Network className="w-5 h-5 text-purple-600" />
                            <div>
                              <h4 className="font-medium text-gray-900">
                                UML {uml.diagram_type || 'Diagram'} - Version {uml.version_number ?? uml.version ?? index + 1}
                              </h4>
                              <p className="text-sm text-gray-600">
                                Status: {uml.is_completed ? 'Completed' : 'In Progress'}
                                {uml.completionSeconds && (
                                  <span className="ml-2">• Time: {Math.round((uml.completionSeconds || 0) / 60)}min</span>
                                )}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {uml.savedAt ? new Date(uml.savedAt).toLocaleString() : 'No timestamp'}
                          </span>
                        </div>
                      </div>

                      <div className="p-6 space-y-4">
                        {(uml.classes && uml.classes.length > 0) ? (
                          <UMLDesignViewer
                            classes={uml.classes as any}
                            relationships={uml.relationships as any || []}
                          />
                        ) : (
                          <div className="border border-dashed border-purple-200 rounded-lg py-12 text-center text-sm text-purple-600 bg-purple-50">
                            No UML shapes recorded for this diagram.
                          </div>
                        )}

                        {uml.content && (
                          <div>
                            <h5 className="text-sm font-medium text-gray-700 mb-2">Description</h5>
                            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                              <p className="text-gray-700 whitespace-pre-wrap text-sm leading-relaxed">{uml.content}</p>
                            </div>
                          </div>
                        )}

                        <EvaluationForm
                          sourceTable="uml_design_responses"
                          sourceRecordUuid={uml.uuid || (uml.id ? String(uml.id) : '')}
                          candidateUuid={response.candidate.uuid || response.candidate.id?.toString() || ''}
                          interviewUuid={response.id}
                          title={`UML ${uml.diagram_type || 'Diagram'} - Version ${uml.version_number ?? uml.version ?? index + 1}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : candidateInputsByCategory.uml.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-purple-50 border-b border-purple-200 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Network className="w-5 h-5 text-purple-600" />
                      <h3 className="text-lg font-medium text-gray-900">UML Design Notes</h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {candidateInputsByCategory.uml.map((input, index) => (
                      <div key={input.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Diagram {index + 1}</h4>
                          <span className="text-xs text-gray-500">
                            {input.last_save_at ? new Date(input.last_save_at).toLocaleString() : 'No timestamp'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">{renderTextWithLinks(input.content)}</div>
                        {input.metadata && (
                          <details className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <summary className="text-xs font-medium text-gray-600 cursor-pointer">Additional Data</summary>
                            <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                              {typeof input.metadata === 'string' ? input.metadata : JSON.stringify(input.metadata, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                    <Network className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No UML Design Data</h3>
                    <p className="text-gray-600">The candidate didn't complete any UML design tasks during this interview.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Database Modeling Tab */}
          {activeTab === 'database' && (
            <div className="space-y-6">
              {dataModelingError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Unable to load data modeling responses</p>
                    <p className="text-sm text-red-700">{dataModelingError}</p>
                  </div>
                </div>
              )}

              {dataModelingLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : dataModelingEntries.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Database Modeling Responses ({dataModelingEntries.length})</h3>
                  {dataModelingEntries.map((entry, index) => {
                    const versionNumber = entry.version ?? entry.version_number ?? index + 1
                    const savedTimestamp = entry.savedAt ? new Date(entry.savedAt).toLocaleString() : 'No timestamp'
                    const entities = entry.entities || []
                    const relationships = entry.relationships || []
                    const foreignKeys = entry.foreignKeys || []
                    const comments = entry.comments || []
                    const zoomValue = typeof entry.zoomPan?.zoom === 'number' ? entry.zoomPan.zoom : null
                    const panXValue = typeof entry.zoomPan?.panX === 'number' ? entry.zoomPan.panX : null
                    const panYValue = typeof entry.zoomPan?.panY === 'number' ? entry.zoomPan.panY : null

                    const findEntityById = (entityId?: string) => entities.find(e => `${e.id}` === `${entityId}`)
                    const findAttribute = (entityId?: string, attributeId?: string) => {
                      const entity = findEntityById(entityId)
                      const fields = entity ? (entity.attributes || entity.fields || []) : []
                      return {
                        entity,
                        attribute: fields.find(field => `${field.id}` === `${attributeId}`)
                      }
                    }

                    return (
                      <div key={entry.uuid || entry.id || index} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Database className="w-5 h-5 text-blue-600" />
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  Database Model {versionNumber ? `- Version ${versionNumber}` : ''}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  Status: {entry.isCompleted ? 'Completed' : 'In Progress'}
                                  {entry.completionSeconds && (
                                    <span className="ml-2">• Time: {Math.round(entry.completionSeconds / 60)} min</span>
                                  )}
                                  {entry.wordCount && (
                                    <span className="ml-2">• {entry.wordCount} words</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">{savedTimestamp}</span>
                          </div>
                        </div>

                        <div className="p-6 space-y-6">
                          <DataModelingViewer
                            entities={entities as any}
                            relationships={relationships as any}
                            foreignKeys={foreignKeys as any}
                            comments={comments as any}
                            zoomPan={entry.zoomPan}
                          />

                          {entry.content && (
                            <div>
                              <h5 className="text-sm font-medium text-gray-700 mb-2">Overview</h5>
                              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 text-sm text-gray-700 leading-relaxed">
                                {renderTextWithLinks(entry.content)}
                              </div>
                            </div>
                          )}

                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs text-gray-600">
                            <span className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                              Entities: <span className="font-semibold text-gray-900">{entities.length}</span>
                            </span>
                            <span className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                              Relationships: <span className="font-semibold text-gray-900">{relationships.length}</span>
                            </span>
                            <span className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                              Foreign Keys: <span className="font-semibold text-gray-900">{foreignKeys.length}</span>
                            </span>
                            <span className="px-3 py-2 rounded-lg border border-gray-200 bg-gray-50">
                              Comments: <span className="font-semibold text-gray-900">{comments.length}</span>
                            </span>
                          </div>

                          {foreignKeys.length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-sm font-medium text-gray-700">Foreign Key Relationships</h5>
                              <div className="space-y-2">
                                {foreignKeys.map((fk, fkIndex) => {
                                  const from = findAttribute(fk.fromEntityId, fk.fromAttributeId)
                                  const to = findAttribute(fk.toEntityId, fk.toAttributeId)

                                  return (
                                    <div key={fk.id || fkIndex} className="border border-purple-200 bg-purple-50 rounded-lg px-4 py-3 text-xs text-purple-900">
                                      <p className="font-medium">
                                        {(from.entity?.name || 'Unknown')} → {(to.entity?.name || 'Unknown')}
                                      </p>
                                      <p className="mt-1">
                                        {(from.attribute?.name || 'column')} references {(to.attribute?.name || 'column')}
                                      </p>
                                      {fk.relationshipType && (
                                        <p className="mt-1 text-purple-700">Relationship: {fk.relationshipType}</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {relationships.length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-sm font-medium text-gray-700">Logical Relationships</h5>
                              <div className="space-y-2 text-xs text-gray-700">
                                {relationships.map((rel, relIndex) => {
                                  const fromEntity = findEntityById(rel.fromEntityId)
                                  const toEntity = findEntityById(rel.toEntityId)
                                  return (
                                    <div key={rel.id || relIndex} className="border border-blue-200 bg-blue-50 rounded-lg px-4 py-3">
                                      <p className="font-medium text-blue-900">
                                        {(fromEntity?.name || 'Entity')} → {(toEntity?.name || 'Entity')}
                                      </p>
                                      {rel.type && (
                                        <p className="mt-1 text-blue-700">Type: {rel.type}</p>
                                      )}
                                      {rel.label && (
                                        <p className="mt-1 text-blue-600">Label: {rel.label}</p>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {comments.length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-sm font-medium text-gray-700">Notes & Comments</h5>
                              <div className="space-y-2">
                                {comments.map((comment, commentIndex) => (
                                  <div key={comment.id || commentIndex} className="border border-yellow-200 bg-yellow-50 rounded-lg px-4 py-3 text-xs text-yellow-900">
                                    {renderTextWithLinks(comment.text || '')}
                                    {(comment.createdAt || comment.created_at) && (
                                      <p className="mt-1 text-[10px] text-yellow-700">
                                        {new Date(comment.createdAt || comment.created_at || '').toLocaleString()}
                                      </p>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {(zoomValue !== null || panXValue !== null || panYValue !== null) && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-gray-600">
                              {zoomValue !== null && <span>Zoom: {zoomValue.toFixed(2)}</span>}
                              {panXValue !== null && !Number.isNaN(panXValue) && <span>Pan X: {panXValue}</span>}
                              {panYValue !== null && !Number.isNaN(panYValue) && <span>Pan Y: {panYValue}</span>}
                            </div>
                          )}

                          {(() => {
                            const evaluationRecordUuid = entry.uuid || toOptionalString(entry.id) || ''
                            if (!evaluationRecordUuid) return null
                            return (
                              <div className="pt-4 border-t border-gray-200">
                                <EvaluationDisplay
                                  sourceTable="data_modeling_responses"
                                  sourceRecordUuid={evaluationRecordUuid}
                                  candidateUuid={response.candidate?.uuid || response.candidate?.id?.toString() || ''}
                                  interviewUuid={response.id}
                                  title={`Database Model${versionNumber ? ` - Version ${versionNumber}` : ''}`}
                                />
                              </div>
                            )
                          })()}
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : candidateInputsByCategory.dataModeling.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                  <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
                    <div className="flex items-center space-x-3">
                      <Database className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-medium text-gray-900">Database Modeling Notes</h3>
                    </div>
                  </div>
                  <div className="p-6 space-y-4">
                    {candidateInputsByCategory.dataModeling.map((input, index) => (
                      <div key={input.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h4 className="text-sm font-medium text-gray-900">Version {index + 1}</h4>
                          <span className="text-xs text-gray-500">
                            {input.last_save_at ? new Date(input.last_save_at).toLocaleString() : 'No timestamp'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-700">{renderTextWithLinks(input.content)}</div>
                        {input.metadata && (
                          <details className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3">
                            <summary className="text-xs font-medium text-gray-600 cursor-pointer">Metadata</summary>
                            <pre className="mt-2 text-xs text-gray-600 whitespace-pre-wrap">
                              {typeof input.metadata === 'string' ? input.metadata : JSON.stringify(input.metadata, null, 2)}
                            </pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                    <Database className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Database Modeling Data</h3>
                    <p className="text-gray-600">The candidate didn't complete any database modeling tasks during this interview.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Database Challenge Tab */}
          {activeTab === 'databaseChallenge' && (
            <div className="space-y-6">
              {databaseChallengesError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Unable to load database challenge records</p>
                    <p className="text-sm text-red-700">{databaseChallengesError}</p>
                  </div>
                </div>
              )}

              {databaseChallengesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : databaseChallenges.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Database Challenge Attempts ({databaseChallenges.length})</h3>
                  {databaseChallenges.map((challenge, index) => (
                    <div key={challenge.assignment_uuid} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <div className="bg-purple-50 border-b border-purple-200 px-6 py-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-4">
                            <div className="flex items-center space-x-3">
                              <Zap className="w-5 h-5 text-purple-600" />
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  Challenge {challenge.assignment_order || index + 1}: {challenge.title}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  Difficulty: {challenge.difficulty || 'N/A'} • Database: {challenge.database_type ? challenge.database_type.toUpperCase() : 'Unknown'}
                                </p>
                              </div>
                            </div>
                            {challenge.description && (
                              <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{challenge.description}</p>
                            )}
                          </div>
                          {challenge.submission?.submission_status && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              challenge.submission.submission_status === 'submitted'
                                ? 'bg-blue-100 text-blue-700'
                                : challenge.submission.submission_status === 'evaluated'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-700'
                            }`}>
                              {challenge.submission.submission_status.replace('_', ' ')}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="p-6 space-y-5">
                        <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                          {challenge.assignment_order && <span>Assignment #{challenge.assignment_order}</span>}
                          {challenge.time_limit_minutes && <span>Time Limit: {challenge.time_limit_minutes} min</span>}
                          {challenge.challenge_type && <span>Type: {challenge.challenge_type}</span>}
                        </div>

                        {challenge.schema_setup && (
                          <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <summary className="text-sm font-medium text-gray-700 cursor-pointer">Schema Setup</summary>
                            <pre className="mt-3 text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto">
                              {challenge.schema_setup}
                            </pre>
                          </details>
                        )}

                        {challenge.hints && (
                          <details className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <summary className="text-sm font-medium text-blue-900 cursor-pointer">Hints</summary>
                            <div className="mt-3 space-y-2 text-sm text-blue-800">
                              {Array.isArray(challenge.hints)
                                ? challenge.hints.map((hint: any, hintIndex: number) => (
                                    <p key={hintIndex}>• {typeof hint === 'string' ? hint : JSON.stringify(hint)}</p>
                                  ))
                                : <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(challenge.hints, null, 2)}</pre>}
                            </div>
                          </details>
                        )}

                        {challenge.expected_result && (
                          <details className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <summary className="text-sm font-medium text-green-900 cursor-pointer">Expected Result</summary>
                            <pre className="mt-3 text-xs text-green-800 whitespace-pre-wrap overflow-x-auto">
                              {JSON.stringify(challenge.expected_result, null, 2)}
                            </pre>
                          </details>
                        )}

                        <div className="border border-gray-200 rounded-lg p-4">
                          <h5 className="text-sm font-medium text-gray-800">Candidate Submission</h5>
                          {challenge.submission ? (
                            <div className="mt-3 space-y-4">
                              <div className="flex flex-wrap gap-4 text-xs text-gray-600">
                                {challenge.submission.score !== null && challenge.submission.score !== undefined && (
                                  <span className={`px-2 py-1 rounded-full font-medium ${getScoreColor(challenge.submission.score)}`}>
                                    Score: {challenge.submission.score}
                                  </span>
                                )}
                                {typeof challenge.submission.is_correct === 'boolean' && (
                                  <span>
                                    Outcome: {challenge.submission.is_correct ? 'Correct' : 'Needs Review'}
                                  </span>
                                )}
                                {challenge.submission.submitted_at && (
                                  <span>Submitted: {new Date(challenge.submission.submitted_at).toLocaleString()}</span>
                                )}
                              </div>

                              {challenge.submission.submitted_query && (
                                <div>
                                  <p className="text-xs font-medium text-gray-600 mb-2">Submitted Query</p>
                                  <div className="bg-gray-900 rounded-lg p-4">
                                    <pre className="text-xs text-green-400 whitespace-pre-wrap overflow-x-auto">
                                      <code>{challenge.submission.submitted_query}</code>
                                    </pre>
                                  </div>
                                </div>
                              )}

                              {challenge.submission.execution_result && (
                                <details className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                  <summary className="text-sm font-medium text-gray-700 cursor-pointer">Execution Result</summary>
                                  <pre className="mt-3 text-xs text-gray-600 whitespace-pre-wrap overflow-x-auto">
                                    {JSON.stringify(challenge.submission.execution_result, null, 2)}
                                  </pre>
                                </details>
                              )}

                          {challenge.submission.execution_error && (
                            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                              {challenge.submission.execution_error}
                            </div>
                          )}

                          {(() => {
                            const evaluationRecordUuid = challenge.submission.submission_uuid
                              || challenge.assignment_uuid
                              || challenge.challenge_uuid
                              || ''
                            if (!evaluationRecordUuid) return null
                            return (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <EvaluationDisplay
                                  sourceTable="interviews_responses"
                                  sourceRecordUuid={evaluationRecordUuid}
                                  candidateUuid={response.candidate?.uuid || response.candidate?.id?.toString() || ''}
                                  interviewUuid={response.id}
                                  title={`Database Challenge ${index + 1} Evaluation`}
                                />
                              </div>
                            )
                          })()}
                        </div>
                      ) : (
                        <p className="mt-3 text-sm text-gray-600">No submission saved yet for this challenge.</p>
                      )}

                      {(() => {
                        const evaluationRecordUuid = challenge.submission?.submission_uuid
                          || challenge.assignment_uuid
                          || challenge.challenge_uuid
                          || ''
                        if (!evaluationRecordUuid) return null
                        return (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <EvaluationDisplay
                              sourceTable="interviews_responses"
                              sourceRecordUuid={evaluationRecordUuid}
                              candidateUuid={response.candidate?.uuid || response.candidate?.id?.toString() || ''}
                              interviewUuid={response.id}
                              title={`Database Challenge ${index + 1} Evaluation`}
                            />
                          </div>
                        )
                      })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : candidateInputsByCategory.database.length > 0 ? (
                <div className="space-y-6">
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <div className="bg-purple-50 border-b border-purple-200 px-6 py-4">
                      <div className="flex items-center space-x-3">
                        <Zap className="w-5 h-5 text-purple-600" />
                        <h3 className="text-lg font-medium text-gray-900">Database Challenge Notes</h3>
                      </div>
                    </div>
                    <div className="p-6 space-y-4">
                      {candidateInputsByCategory.database.map((input, inputIndex) => (
                        <div key={input.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <h4 className="text-sm font-medium text-gray-900">Entry {inputIndex + 1}</h4>
                            <span className="text-xs text-gray-500">
                              {input.last_save_at ? new Date(input.last_save_at).toLocaleString() : 'No timestamp'}
                            </span>
                          </div>
                          <pre className="text-sm text-gray-700 whitespace-pre-wrap">{input.content}</pre>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                    <Zap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Database Challenge Data</h3>
                    <p className="text-gray-600">The candidate did not attempt any database challenges for this interview.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Q&A Responses Tab */}
          {activeTab === 'responses' && (
            <div className="space-y-6">
              {candidateInputsByCategory.qa.length > 0 ? (
                candidateInputsByCategory.qa.map((input, index) => (
                  <div key={input.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-medium text-gray-900">{input.type || 'Response'} {index + 1}</h4>
                      <span className="text-xs text-gray-500">
                        {input.last_save_at ? new Date(input.last_save_at).toLocaleString() : 'No timestamp'}
                      </span>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">Content:</p>
                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700">
                        {renderTextWithLinks(input.content)}
                      </div>
                    </div>
                    
                    {input.problem_title && (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-2">Related Problem:</p>
                        <p className="text-sm text-gray-600">{input.problem_title}</p>
                      </div>
                    )}
                    
                    {input.file_url && (
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Attachment:</p>
                        <a href={input.file_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 text-sm">
                          View File
                        </a>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No Q&A responses available</p>
                </div>
              )}
            </div>
          )}

          {/* Whiteboard Tab */}
          {activeTab === 'whiteboard' && (
            <div className="space-y-6">
              {whiteboardError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-800">Unable to load whiteboard responses</p>
                    <p className="text-sm text-red-700">{whiteboardError}</p>
                  </div>
                </div>
              )}

              {whiteboardLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-600" />
                </div>
              ) : whiteboardEntries.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Whiteboard Sessions ({whiteboardEntries.length} entries)</h3>
                  {whiteboardEntries.map((wb, index) => {
                    const versionLabel = wb.version ?? wb.version_number ?? wb.versionNumber ?? index + 1
                    const savedAt = wb.saved_at ?? wb.savedAt ?? null
                    const actionType = wb.action_type ?? 'auto'
                    const category = wb.input_category ?? 'whiteboard'
                    const recordId = toOptionalString(wb.id, String(index)) || `${index}`
                    const evaluationRecordUuid = wb.id ?? wb.uuid ?? recordId

                    return (
                      <div key={recordId} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                        <div className="bg-orange-50 border-b border-orange-200 px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <Palette className="w-5 h-5 text-orange-600" />
                              <div>
                                <h4 className="font-medium text-gray-900">
                                  Whiteboard Session {index + 1} - Version {versionLabel}
                                </h4>
                                <p className="text-sm text-gray-600">
                                  Action: {actionType} • Category: {category || 'General'}
                                </p>
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {savedAt ? new Date(savedAt).toLocaleString() : 'No timestamp'}
                            </span>
                          </div>
                        </div>

                        <div className="p-6">
                          <WhiteboardViewer
                            canvasElements={wb.canvas_elements ?? wb.canvasElements}
                            content={wb.content}
                            whiteboardTextDiscussion={wb.whiteboard_text_discussion ?? wb.whiteboardTextDiscussion ?? null}
                            strokeData={wb.stroke_data ?? wb.strokeData}
                          />

                          <div className="mt-6 pt-6 border-t border-gray-200">
                          <EvaluationDisplay
                            sourceTable="whiteboard_responses"
                            sourceRecordUuid={evaluationRecordUuid.toString()}
                            candidateUuid={response.candidate.uuid || response.candidate.id?.toString() || ''}
                            interviewUuid={response.id}
                            title={`Whiteboard Session ${index + 1} - Version ${versionLabel}`}
                          />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="bg-gray-50 rounded-lg p-8 max-w-md mx-auto">
                    <Palette className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Whiteboard Data</h3>
                    <p className="text-gray-600">The candidate didn't use the whiteboard feature during this interview.</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="space-y-6">
              {candidateInputsByCategory.notes.length > 0 ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">Candidate Notes ({candidateInputsByCategory.notes.length})</h3>
                  {candidateInputsByCategory.notes.map((note, index) => (
                    <div key={note.id || index} className="bg-white border border-gray-200 rounded-lg p-5">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">Note {index + 1}</h4>
                          <p className="text-xs text-gray-500">
                            {(note.category || note.type) ? (note.category || note.type) : 'General'}
                            {note.save_type ? ` • ${note.save_type}` : ''}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500">
                          {note.last_save_at ? new Date(note.last_save_at).toLocaleString() : 'No timestamp'}
                        </span>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                        {note.content || 'No content provided.'}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No notes captured for this interview yet.</p>
                </div>
              )}
            </div>
          )}

          {/* Recording Tab */}
          {activeTab === 'recording' && (
            <div className="space-y-6">
              {response.recordings && response.recordings.length > 0 ? (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Interview Recordings ({response.recordings.length})</h3>
                  {response.recordings.map((recording, index) => (
                    <div key={recording.id} className="bg-white border border-gray-200 rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h4 className="font-medium text-gray-900">{recording.type} Recording</h4>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          recording.status === 'completed' ? 'bg-green-100 text-green-800' :
                          recording.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {recording.status}
                        </span>
                      </div>
                      
                      <div className="bg-gray-900 rounded-lg p-6 text-center mb-4">
                        <Video className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                        <p className="text-gray-300 mb-4">Video player would be embedded here</p>
                        <div className="flex justify-center items-center space-x-4">
                          <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700">
                            <Play className="w-4 h-4" />
                          </button>
                          <span className="text-gray-300">
                            Duration: {recording.duration_seconds ? Math.round(recording.duration_seconds / 60) : 0} minutes
                          </span>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                        <div>File Size: {recording.file_size ? `${Math.round(recording.file_size / 1024 / 1024)}MB` : 'Unknown'}</div>
                        <div>Recorded: {recording.recorded_at ? new Date(recording.recorded_at).toLocaleString() : 'Unknown'}</div>
                      </div>
                      
                      {recording.file_url && (
                        <div className="mt-4">
                          <a 
                            href={recording.file_url} 
                            target="_blank" 
                            rel="noopener noreferrer" 
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            Download Recording
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="bg-gray-100 rounded-lg p-8">
                    <Video className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-600">No recordings available</p>
                    <p className="text-sm text-gray-500 mt-2">This interview was not recorded</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Share Interview Modal */}
      <ShareInterviewModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        interview={response}
        onShared={() => {
          // Could refresh shared interviews or show success message
          console.log('Interview shared successfully')
        }}
      />
    </div>
  )
}
